<template>
  <div class="flex flex-col">
    <div v-if="picklistOptions !== undefined" :style="styleObject">
      <VueDraggableNext
        v-model="localPicklistOptions"
        handle=".drag-handle"
        class="w-full"
        :style="{ marginTop: field.style === 'bubble' && !isActive ? '6px' : '0px' }"
      >
        <div
          v-for="(pickerOption, index) in localPicklistOptions"
          :key="index"
          :style="minimumWidth"
          class="option"
          :class="{ 'option-bubble': field.style === 'bubble' }"
        >
          <div class="align-together flex items-start">
            <Menu05Icon
              v-if="isActive"
              class="drag-handle ml-0 mr-2 mt-1 h-4 w-4 flex-shrink-0 cursor-move text-gray-500"
            />
            <div v-if="field.style === 'bubble'" class="bubble-container form-control w-auto">
              <input
                v-if="type === 'input'"
                :id="`checkbox-${field.id}-${index}`"
                type="checkbox"
                :readonly="readonly"
                :value="typeof pickerOption === 'string' ? pickerOption : pickerOption.label"
                disabled
              />
              <label
                :id="`option-label-${field.id}-${index}`"
                :ref="(el) => setLabelRef(el as HTMLElement, index)"
                :for="null"
                contenteditable="true"
                class="bubble-label"
                :style="[syncedLabelInlineStyle]"
                @beforeinput="measureAndSyncLabelWidths()"
                @input="
                  (e) => {
                    updateOptionLabel(e, index);
                    handleSafariCursor(e);
                    measureAndSyncLabelWidths();
                  }
                "
                @compositionend="measureAndSyncLabelWidths()"
                @paste="() => measureAndSyncLabelWidths()"
                @keydown="(e) => e.key !== 'Tab' && handleSafariCursor(e)"
                @keydown.capture="
                  (e) =>
                    handleOptionTabNavigation(e, index, localPicklistOptions.length, getLabelRef)
                "
                @keydown.enter.prevent.stop="handleEnterOnOption(index)"
                @keyup.enter.prevent.stop
                @keyup.tab.prevent.stop
                @keyup.capture="suppressTabKeyUp"
                @focus="handleFocus"
                @keyup="
                  (e) => {
                    handleEditing(e, index);
                    measureAndSyncLabelWidths();
                  }
                "
              >
                {{ typeof pickerOption === "string" ? pickerOption : pickerOption.label }}
              </label>
            </div>
            <template v-else>
              <input
                v-if="type === 'input'"
                :id="`checkbox-${field.id}-${index}`"
                class="ml-0 mt-1 flex-shrink-0"
                type="checkbox"
                :readonly="readonly"
                :value="typeof pickerOption === 'string' ? pickerOption : pickerOption.label"
                disabled
              />
              <label
                :id="`option-label-${field.id}-${index}`"
                :for="null"
                contenteditable="true"
                class="inline-block w-auto flex-grow cursor-text border-0 pr-2 shadow-none outline-none"
                @input="
                  (e) => {
                    updateOptionLabel(e, index);
                    handleSafariCursor(e);
                  }
                "
                @keydown="(e) => e.key !== 'Tab' && handleSafariCursor(e)"
                @keydown.capture="
                  (e) =>
                    handleOptionTabNavigation(e, index, localPicklistOptions.length, getLabelRef)
                "
                @keydown.enter.prevent.stop="handleEnterOnOption(index)"
                @keyup.enter.prevent.stop
                @keyup.tab.prevent.stop
                @keyup.capture="suppressTabKeyUp"
                @focus="handleFocus"
                @keyup="handleEditing($event, index)"
              >
                {{ typeof pickerOption === "string" ? pickerOption : pickerOption.label }}
              </label>
            </template>
            <Trash01Icon
              v-if="isActive && !(field?.model && field.model.startsWith('custom_objects'))"
              class="ml-2 mt-1 h-4 w-4 flex-shrink-0 cursor-pointer text-gray-500"
              @click="prepareToRemoveOption(index)"
            />
          </div>
        </div>
      </VueDraggableNext>
    </div>
    <div v-if="isActive" class="mt-[-1rem] flex items-center">
      <UIButton id="add-option" text class="mt-4" :disabled="isLastOptionEmpty" @click="addOption">
        <PlusIcon class="mr-1.5 h-5 w-5" />
        {{ t("common.addOption") }}
      </UIButton>
    </div>
    <template v-if="isActive">
      <div
        ref="shortLabelElement"
        contenteditable="true"
        class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
        :data-placeholder="t('common.shortLabelPlaceholder')"
        @blur="updateShortLabel"
        @input="handleShortLabelInput"
        @keydown="handleSafariCursor"
        @click="(e) => e.stopPropagation()"
        @focus="(e) => e.stopPropagation()"
        v-text="shortLabel"
      ></div>
    </template>
    <template v-else>
      <span v-if="field.shortLabel" class="short-label font-inter break-words text-sm">
        {{ field.shortLabel }}
      </span>
    </template>
  </div>
  <DeleteConfirmationModal
    :show="showDeleteModal"
    @confirm="removeOption"
    @cancel="cancelRemoveOption"
  />
</template>

<script setup lang="ts">
import { CONSTANTS } from "@/util/constants";
import {
  fixSafariCursor,
  getCaretOffsetWithin,
  handleOptionTabNavigation,
  handleShortLabelInput as handleShortLabelInputUtil,
  setCaretOffsetWithin,
  suppressTabKeyUp,
} from "@/util/methods";
import { Menu05Icon, PlusIcon, Trash01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton } from "@gohighlevel/ghl-ui";
import { cloneDeep, isEqual } from "lodash";
import { computed, CSSProperties, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import DeleteConfirmationModal from "./DeleteConfirmationModal.vue";

const { t } = useI18n();

interface PickerOption {
  src?: string;
  label?: string;
  [key: string]: any;
}

const props = defineProps({
  type: {
    type: String,
    default: "input",
  },
  placeholder: {
    type: String,
    default: "",
  },
  picklistOptions: {
    type: Array as () => (string | PickerOption)[],
    default: () => {
      return [];
    },
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  layout: {
    type: Number,
    default: 1,
  },
  field: {
    type: Object,
    default() {
      return {};
    },
  },
  styleObject: {
    type: Object as () => CSSProperties,
    default: () => ({}),
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  customFieldCreated: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:shortLabel", "update:picklistOptions"]);

const shortLabel = ref(props.field.shortLabel || "");

// Add local state for the draggable component
const localPicklistOptions = ref([...props.picklistOptions]);

// Add bidirectional watchers with flag to prevent recursive updates
const isInternalUpdate = ref(false);

watch(
  () => props.picklistOptions,
  (newVal) => {
    if (!isInternalUpdate.value && !isEqual(newVal, localPicklistOptions.value)) {
      localPicklistOptions.value = cloneDeep(newVal);
    }
    isInternalUpdate.value = false;
  },
  { deep: true, immediate: true }
);

watch(
  () => localPicklistOptions.value,
  (newVal) => {
    if (!isEqual(newVal, props.picklistOptions)) {
      isInternalUpdate.value = true;
      emit("update:picklistOptions", cloneDeep(newVal));
    }
  },
  { deep: true }
);

watch(
  () => props.field.shortLabel,
  (newVal) => {
    shortLabel.value = newVal || "";
  }
);

// Keep bubble labels width in sync with the widest label
const labelRefs = ref<HTMLElement[]>([]);
const setLabelRef = (el: HTMLElement | null, index: number): void => {
  if (el) {
    labelRefs.value[index] = el;
  }
};
const getLabelRef = (i: number): HTMLElement | null => labelRefs.value[i] || null;

const syncedLabelWidth = ref<number>(0);
const measureAndSyncLabelWidths = (): void => {
  if (syncedLabelWidth.value !== 0) {
    syncedLabelWidth.value = 0;
  }
  nextTick(() => {
    let maxWidth = 0;
    for (const el of labelRefs.value) {
      if (!el) {
        continue;
      }
      const prevMinWidth = el.style.minWidth;
      el.style.minWidth = "0px";
      const width = Math.ceil(el.scrollWidth);
      el.style.minWidth = prevMinWidth;
      if (width > maxWidth) {
        maxWidth = width;
      }
    }
    syncedLabelWidth.value = maxWidth;
  });
};
onMounted(() => {
  measureAndSyncLabelWidths();
  window.addEventListener("resize", measureAndSyncLabelWidths);
});
onUnmounted(() => {
  window.removeEventListener("resize", measureAndSyncLabelWidths);
});
watch(
  () => localPicklistOptions.value,
  () => {
    measureAndSyncLabelWidths();
  },
  { deep: true }
);
watch(
  () => props.field?.style,
  () => {
    measureAndSyncLabelWidths();
  }
);
const syncedLabelInlineStyle = computed((): CSSProperties => {
  if (props.field?.style === "bubble") {
    const BASE_MIN = 20; // px
    const targetMin = Math.max(BASE_MIN, syncedLabelWidth.value || 0);
    return { minWidth: `${targetMin}px`, maxWidth: "100%" } as CSSProperties;
  }
  return {} as CSSProperties;
});

const indexToRemove = ref<number | null>(null);
const showDeleteModal = ref(false);

const getWidth = () => {
  let width = 100;
  if (props.layout === CONSTANTS.TWO_COLUMN) {
    width = Math.max(width / props.field.columnsNumber, 50);
  } else {
    width = Math.max(width / props.field.columnsNumber, 20);
  }
  if (props.field.spreadColumns) {
    return width + "%";
  }
  return "100%";
};
const minimumWidth = computed((): CSSProperties => {
  const width = getWidth();
  return {
    width,
  };
});

const updateOptionLabel = (event: Event, index: number) => {
  const target = event.target as HTMLElement;
  const text = target.innerText.replace(/\u200B/g, "");
  const caret = getCaretOffsetWithin(target);

  if (typeof localPicklistOptions.value[index] === "string") {
    (localPicklistOptions.value as (string | PickerOption)[])[index] = text;
  } else {
    (localPicklistOptions.value[index] as PickerOption).label = text;
  }

  nextTick(() => {
    const el = labelRefs.value[index] || target;
    setCaretOffsetWithin(el, caret);
  });
};

const prepareToRemoveOption = (index: number) => {
  if (props.picklistOptions.length > 1) {
    if (props.customFieldCreated) {
      indexToRemove.value = index;
      showDeleteModal.value = true;
    } else {
      const newOptions = [...localPicklistOptions.value];
      newOptions.splice(index, 1);
      localPicklistOptions.value = newOptions;
    }
  }
};

const removeOption = () => {
  if (indexToRemove.value !== null) {
    const newOptions = [...localPicklistOptions.value];
    newOptions.splice(indexToRemove.value, 1);
    localPicklistOptions.value = newOptions;
  }
  showDeleteModal.value = false;
};

const cancelRemoveOption = () => {
  indexToRemove.value = null;
  showDeleteModal.value = false;
};

const isLastOptionEmpty = computed(() => {
  const lastOption = props.picklistOptions[props.picklistOptions.length - 1];
  if (typeof lastOption === "string") {
    return lastOption.trim() === "";
  }
  return lastOption?.label?.trim() === "";
});

const addOption = () => {
  addOptionAt((localPicklistOptions.value?.length ?? 1) - 1);
};

const addOptionAt = (index: number) => {
  const newOptions = [...localPicklistOptions.value];
  const insertAt = Math.min(Math.max(index + 1, 0), newOptions.length);
  if (typeof newOptions[0] === "string") {
    newOptions.splice(insertAt, 0, t("common.typeAnOption"));
  } else {
    newOptions.splice(insertAt, 0, { id: `option-${Date.now()}`, label: t("common.typeAnOption") });
  }
  localPicklistOptions.value = newOptions;
  nextTick(() => {
    const resolvedLabel =
      labelRefs.value[insertAt] ||
      (document.getElementById(`option-label-${props.field.id}-${insertAt}`) as HTMLElement | null);
    if (resolvedLabel) {
      resolvedLabel.focus();
      const range = document.createRange();
      range.selectNodeContents(resolvedLabel);
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  });
};

const handleFocus = (event: FocusEvent) => {
  const target = event.target as HTMLElement;

  const selection = window.getSelection();
  if (selection) {
    selection.removeAllRanges();
  }

  const sanitized = (target.textContent || "").replace(/\u200B/g, "").trim();
  if (!sanitized) {
    let textNode = target.firstChild as Text | null;
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
      target.textContent = "";
      textNode = document.createTextNode("\u200B");
      target.appendChild(textNode);
    }
    if (!textNode.textContent) {
      textNode.textContent = "\u200B";
    }
    const range = document.createRange();
    range.setStart(textNode, 0);
    range.collapse(true);
    selection?.addRange(range);
    return;
  }

  const range = document.createRange();
  range.selectNodeContents(target);
  selection?.addRange(range);
};

const shortLabelElement = ref<HTMLElement | null>(null);

const updateShortLabel = () => emit("update:shortLabel", shortLabel.value);

const handleShortLabelInput = (e: Event) =>
  handleShortLabelInputUtil(e, shortLabel, updateShortLabel);

const handleEditing = (event: KeyboardEvent, index: number) => {
  const target = event.target as HTMLElement;

  if (target.textContent?.trim() === "") {
    if (target.textContent) {
      target.textContent = "";

      const range = document.createRange();
      range.setStart(target, 0);
      range.collapse(true);

      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }
};

const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

const handleEnterOnOption = (index: number) => {
  addOptionAt(index);
};
</script>

<style scoped>
.option {
  position: relative;
  display: inline-block;
  widows: 100%;
}
.option label {
  line-height: 30px;
  display: block;
  margin-left: 10px;
}
.short-label {
  background-color: transparent;
  margin-top: 4px;
  font-size: 14px;
  color: #4b5563;
}

.short-label:empty:before {
  content: attr(data-placeholder);
}

/* Bubble Style Overrides for Checkboxes */
.option-bubble {
  margin-bottom: 8px;
}

.bubble-container {
  background: transparent;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  /* Keep checkbox and label on the same line so the checkbox stays
     vertically centered relative to multi-line labels */
  flex-wrap: nowrap;
  /* Add small horizontal spacing between bubbles */
  margin-left: 2px;
  margin-right: 2px;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  cursor: pointer;
}

/* Consistent checkbox visuals (bubble + standard) */
.option:not(.option-bubble) input[type="checkbox"],
.bubble-container input[type="checkbox"] {
  -webkit-appearance: none;
  appearance: none;
  width: 15px;
  height: 15px;
  border: 1px solid #8b8b8b;
  border-radius: 3px;
  background: #ffffff;
  display: inline-block;
  position: relative;
  margin: 0;
  flex-shrink: 0;
}
.option:not(.option-bubble) input[type="checkbox"]:checked,
.bubble-container input[type="checkbox"]:checked {
  background-color: #000000;
  border-color: #000000;
}

.bubble-container:hover {
  border-color: #d1d5db;
}

.bubble-container input[type="checkbox"]:checked ~ .bubble-label {
  color: #1e40af;
}

.bubble-label {
  background: transparent !important;
  border: none !important;
  padding: 0;
  line-height: 16px;
  display: flex;
  align-items: center;
  min-height: 16px;
  margin: 0 !important;
  cursor: text;
  outline: none;
  color: inherit;
  max-width: 100%;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.option-bubble .align-together {
  align-items: center !important;
}
.option-bubble .drag-handle,
.option-bubble .ml-2.mt-1 {
  margin-top: 0 !important;
  align-self: center;
}

.option:not(.option-bubble) .align-together {
  align-items: center !important;
}

.option:not(.option-bubble) .drag-handle,
.option:not(.option-bubble) input[type="checkbox"],
.option:not(.option-bubble) .ml-2.mt-1 {
  margin-top: 0 !important;
}

.option label {
  margin-bottom: 0 !important;
}
</style>
